import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const CalculatorResults = ({ estimate, projectDetails }) => {
  const navigate = useNavigate();

  const handleSignup = () => {
    // Format the estimate data properly
    const estimateData = {
      amount: Number(estimate), // Ensure it's a number
      projectDetails: {
        service: projectDetails.service,
        dimensions: `Length: ${projectDetails.length}'`,
        area: `${projectDetails.area} sq ft`,
        zipCode: projectDetails.zipCode
      }
    };

    // Stringify the data properly
    localStorage.setItem('pendingEstimate', JSON.stringify(estimateData));
    
    // Log to verify the data
    console.log('Saved estimate:', estimateData);
    
    navigate('/homeowners-signup?from=calculator');
  };

  const handleRequestCallback = () => {
    localStorage.setItem('tempEstimate', JSON.stringify({
      estimate,
      projectDetails,
      timestamp: new Date().toISOString()
    }));
    
    navigate('/contact?type=callback');
  };

  return (
    <ResultsContainer>
      <Title>Your Estimate</Title>
      
      <EstimateCard>
        <EstimateAmount>${estimate.toLocaleString()}</EstimateAmount>
        
        {projectDetails.projectImage && (
          <ProjectImageContainer>
            <ProjectImage src={projectDetails.projectImage} alt={projectDetails.service} />
          </ProjectImageContainer>
        )}

        {projectDetails.projectDescription && (
          <ProjectDescription>
            {projectDetails.projectDescription}
          </ProjectDescription>
        )}

        <EstimateDetails>
          {Object.entries(projectDetails)
            .filter(([key]) => !['projectImage', 'projectDescription'].includes(key))
            .map(([key, value]) => (
              <DetailItem key={key}>
                <DetailLabel>{key}:</DetailLabel>
                <DetailValue>{value}</DetailValue>
              </DetailItem>
            ))}
        </EstimateDetails>

        <ButtonGroup>
          <SaveButton 
            onClick={handleSignup}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Save Estimate
          </SaveButton>
          <CallbackButton 
            onClick={handleRequestCallback}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Request Callback
          </CallbackButton>
        </ButtonGroup>
      </EstimateCard>

      <RecalculateButton onClick={() => window.location.reload()}>
        Recalculate
      </RecalculateButton>

      <Disclaimer>
        * This is an estimated price. Final cost may vary based on specific requirements and site conditions.
      </Disclaimer>
    </ResultsContainer>
  );
};

const ResultsContainer = styled.div`
  max-width: 600px;
  margin: 2rem auto;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 24px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Title = styled.h2`
  font-size: 2rem;
  color: white;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const EstimateCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const EstimateAmount = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: var(--primary);
  text-align: center;
  margin-bottom: 1.5rem;
  text-shadow: 0 0 20px rgba(255, 107, 0, 0.3);
`;

const EstimateDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
`;

const DetailLabel = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
`;

const DetailValue = styled.span`
  color: white;
  font-weight: 500;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SaveButton = styled(motion.button)`
  padding: 1rem;
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    box-shadow: 0 0 20px rgba(255, 107, 0, 0.3);
  }
`;

const CallbackButton = styled(motion.button)`
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const RecalculateButton = styled.button`
  padding: 1rem;
  background: none;
  color: var(--primary);
  border: 1px solid var(--primary);
  border-radius: 12px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 107, 0, 0.1);
  }
`;

const Disclaimer = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  text-align: center;
  margin-top: 2rem;
  font-style: italic;
`;

const ProjectImageContainer = styled.div`
  margin: 1.5rem 0;
  border-radius: 12px;
  overflow: hidden;
`;

const ProjectImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const ProjectDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  line-height: 1.6;
  margin: 1rem 0;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
`;

export default CalculatorResults; 